import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../components/ToastProvider';
import { InfraNodesPanel } from '../components/InfraNodesPanel';
import { OvpnServersPanel } from '../components/OvpnServersPanel';
import { api } from '../services/api';
import type { ServerHealth, ServerListItem } from '../services/types';
import { notifyApiResult } from '../utils/notify';
import { useAuth } from '../hooks/useAuth';
import { pluralServers } from '../utils/format';

type ServersTab = 'regions' | 'nodes' | 'ovpn';

export function ServersPage() {
  const { user } = useAuth();
  const toast = useToast();
  const canWrite = user?.role === 'admin' || user?.role === 'superadmin';
  const [tab, setTab] = useState<ServersTab>('regions');

  const [servers, setServers] = useState<ServerListItem[]>([]);
  const [healthMap, setHealthMap] = useState<Record<string, ServerHealth>>({});
  const [loading, setLoading] = useState(true);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    setError(null);
    const res = await api.listServers();
    if (res.data) setServers(res.data);
    else if (res.error) {
      setError(res.error.message);
      if (!background) toast.error(res.error.message);
    }
    if (!background) setLoading(false);
  }, [toast]);

  useEffect(() => {
    if (tab === 'regions') void load();
  }, [load, tab]);

  const checkHealth = async (code: string) => {
    setBusyCode(code);
    const res = await api.getServerHealth(code);
    if (res.data) {
      setHealthMap((prev) => ({ ...prev, [code]: res.data! }));
    } else if (res.error) {
      toast.error(res.error.message);
    }
    setBusyCode(null);
  };

  const reloadConfig = async () => {
    setMessage(null);
    setError(null);
    const res = await api.reloadServersConfig();
    if (
      notifyApiResult(toast, res, (data) => {
        const parts = [pluralServers(data.servers_count)];
        if (data.ovpn_servers_count != null) {
          parts.push(`${data.ovpn_servers_count} OVPN`);
        }
        return `Конфиг перезагружен (${parts.join(', ')})`;
      })
    ) {
      await load(true);
      setMessage(`Конфиг перезагружен`);
    } else if (res.error) setError(res.error.message);
  };

  const syncReplicas = async (code: string) => {
    if (!window.confirm(`Синхронизировать реплики для ${code}?`)) return;
    setBusyCode(code);
    setMessage(null);
    const res = await api.syncServerReplicas(code);
    if (notifyApiResult(toast, res, (data) => `${code}: ${data.message}`)) {
      setMessage(`${code}: ${res.data!.message}`);
    } else if (res.error) {
      setError(res.error.message);
    }
    setBusyCode(null);
  };

  return (
    <div>
      <div className="page-header">
        <h1>VPN серверы</h1>
        <Link to="/vpn" className="btn btn-ghost">
          ← Ключи
        </Link>
      </div>

      <div className="tabs">
        <button
          type="button"
          className={`tab ${tab === 'regions' ? 'active' : ''}`}
          onClick={() => setTab('regions')}
        >
          Регионы
        </button>
        <button
          type="button"
          className={`tab ${tab === 'nodes' ? 'active' : ''}`}
          onClick={() => setTab('nodes')}
        >
          Ноды
        </button>
        <button
          type="button"
          className={`tab ${tab === 'ovpn' ? 'active' : ''}`}
          onClick={() => setTab('ovpn')}
        >
          OpenVPN
        </button>
      </div>

      {tab === 'nodes' ? (
        <InfraNodesPanel canWrite={canWrite} />
      ) : tab === 'ovpn' ? (
        <OvpnServersPanel canWrite={canWrite} />
      ) : (
        <>
          {canWrite && (
            <div style={{ marginBottom: '1rem' }}>
              <button type="button" className="btn btn-primary" onClick={reloadConfig}>
                Перезагрузить конфиг из БД
              </button>
            </div>
          )}

          {loading && servers.length === 0 && (
            <p style={{ color: 'var(--text-muted)' }}>Загрузка…</p>
          )}
          {error && <p style={{ color: 'var(--danger)' }}>{error}</p>}
          {message && <p style={{ color: 'var(--success)' }}>{message}</p>}

          {!loading && servers.length === 0 && (
            <p style={{ color: 'var(--text-muted)' }}>В базе нет VPN-серверов (таблица servers пуста).</p>
          )}

          {servers.length > 0 && (
            <div style={{ display: 'grid', gap: '1rem' }}>
              {servers.map((srv) => {
                const health = healthMap[srv.code];
                const busy = busyCode === srv.code;
                return (
                  <div key={srv.code} className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                      <div>
                        <h3 style={{ margin: '0 0 0.5rem' }}>
                          <code>{srv.code}</code>
                          {srv.country ? ` · ${srv.country.toUpperCase()}` : ''}
                        </h3>
                        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                          Tier: {srv.subscription_tier} · Активных ключей: {srv.active_keys}
                          {!srv.enabled && ' · выключен'}
                          {' · '}
                          <span
                            title={
                              srv.wg_nodes_source === 'db'
                                ? 'Ноды из infra_nodes (БД)'
                                : srv.wg_nodes_source === 'env'
                                  ? 'Ноды из WG_SERVERS (.env)'
                                  : 'WG nodes не настроены'
                            }
                          >
                            source: {srv.wg_nodes_source || 'none'}
                          </span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                        <button
                          type="button"
                          className="btn btn-ghost"
                          disabled={busy}
                          onClick={() => checkHealth(srv.code)}
                        >
                          Health check
                        </button>
                        {canWrite && srv.wg_nodes.some((n) => !n.is_primary) && (
                          <button
                            type="button"
                            className="btn btn-ghost"
                            disabled={busy}
                            onClick={() => syncReplicas(srv.code)}
                          >
                            Sync replicas
                          </button>
                        )}
                      </div>
                    </div>

                    {health && (
                      <div
                        style={{
                          marginTop: '0.75rem',
                          padding: '0.75rem',
                          background: 'var(--bg)',
                          borderRadius: 'var(--radius)',
                          fontSize: '0.875rem',
                        }}
                      >
                        SSH: {health.ssh_ok ? '✓' : '✗'} · API: {health.api_ok ? '✓' : '✗'}
                        {health.live_clients_count != null && ` · Клиентов: ${health.live_clients_count}`}
                        {health.error && (
                          <span style={{ color: 'var(--danger)', marginLeft: 8 }}>{health.error}</span>
                        )}
                      </div>
                    )}

                    {srv.wg_nodes.length > 0 && (
                      <div className="table-wrap" style={{ marginTop: '0.75rem' }}>
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>Node</th>
                              <th>IP</th>
                              <th>URL</th>
                              <th>Primary</th>
                            </tr>
                          </thead>
                          <tbody>
                            {srv.wg_nodes.map((node) => (
                              <tr key={`${node.country}-${node.number}`}>
                                <td>
                                  {node.country}-{node.number}
                                </td>
                                <td>{node.ip}</td>
                                <td>
                                  <code style={{ fontSize: '0.75rem' }}>{node.base_url}</code>
                                </td>
                                <td>{node.is_primary ? 'да' : '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {srv.wg_nodes.length === 0 && (
                      <p style={{ margin: '0.75rem 0 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                        WG nodes не настроены (добавьте во вкладке «Ноды» или в WG_SERVERS)
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
